import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

/**
 * Interactive Map page intro: a centered heading + page blurb that sits
 * between the hero/wave band and the interactive map embed. Mirrors the
 * FloorPlansIntro / VirtualTourIntro copy-first pattern (heading + body,
 * no CTA) so Find Your Home pages share the same opening rhythm.
 */
export function InteractiveMapIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-10 md:pb-14"
    >
      <div className="mx-auto px-3 sm:px-4 lg:px-6 text-center">
        <RichTextContent
          path="interactivemapintro.heading"
          label="Heading"
          defaultValue='<p>Explore <span data-type="field-token" data-field="property_name"></span></p>'
          as="h2"
          className="font-heading text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] font-semibold uppercase tracking-wide text-[var(--vibe-color-primary)] leading-[1.1] mb-6"
        />

        <div className="max-w-3xl mx-auto">
          <RichTextContent
            path="interactivemapintro.body"
            label="Body Text"
            defaultField="property_interactivemap_blurb"
            defaultValue={`<p>Use our interactive map to browse available homes at <span data-type="field-token" data-field="property_name"></span>. Click a unit to see floor plans, pricing, and availability — then schedule a tour when you're ready to visit in person.</p>`}
            as="div"
            className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline"
          />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapIntro',
  displayName: 'Interactive Map Intro',
  tokens: {
    'interactivemapintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p>Explore <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'interactivemapintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_interactivemap_blurb',
      defaultValue:
        '<p>Use our interactive map to browse available homes at <span data-type="field-token" data-field="property_name"></span>. Click a unit to see floor plans, pricing, and availability — then schedule a tour when you\'re ready to visit in person.</p>',
    },
  },
};

(InteractiveMapIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapIntro;
